<script lang="ts">
  // Duel comms panel. DOCKED as its own bordered section at the bottom of the card-info rail (always
  // solid, never transparent, so it doesn't camouflage against the card inspector above it). On narrow
  // screens where the rail is hidden it falls back to a compact bottom-left panel. Two tabs: "Chat"
  // (player messages) and "Log" (the play-by-play game events).
  import { fly } from "svelte/transition";
  import { duel, sendDuelSay } from "../../stores/duel.svelte.ts";

  let { docked = false }: { docked?: boolean } = $props();

  const TABS = [{ k: "chat", label: "Chat" }, { k: "log", label: "Log" }] as const;

  // The FLOATING panel (3D / narrow layouts, where there's no rail) is dismissible — it collapses to a
  // small hex launcher so it never sits over the board. Persisted; the docked rail panel is always open.
  // Keep the tactical field clear on first use; players who open comms keep that preference.
  let open = $state(typeof localStorage !== "undefined" && localStorage.getItem("df.chatopen") === "1");
  function setOpen(v: boolean) { open = v; try { localStorage.setItem("df.chatopen", v ? "1" : "0"); } catch { /* private */ } }
  let tab = $state<"chat" | "log">("chat");
  let draft = $state("");
  let flashing = $state(false);
  let chatUnread = $state(0);
  let flashTimer: ReturnType<typeof setTimeout>;
  let scroller = $state<HTMLDivElement | null>(null);

  // The panel is always solid now, so a new CHAT message just pulses the feed's border accent (and
  // bumps the unread badge when you're on the Log tab). Log lines tick constantly and don't pulse.
  let lastChat = 0;
  $effect(() => {
    const c = duel.chat.length;
    if (c > lastChat) {
      flashing = true;
      clearTimeout(flashTimer);
      flashTimer = setTimeout(() => (flashing = false), 2200);
      if (tab !== "chat" || (!open && !docked)) chatUnread += c - lastChat;
    }
    lastChat = c;
  });
  // Keep the newest line in view whenever the feed or the tab changes.
  $effect(() => {
    void duel.chat.length; void duel.log.length; void tab;
    queueMicrotask(() => { if (scroller) scroller.scrollTop = scroller.scrollHeight; });
  });

  function pick(k: "chat" | "log") { tab = k; if (k === "chat") chatUnread = 0; }
  function send() {
    const text = draft.trim();
    if (!text) return;
    sendDuelSay(text);
    draft = "";
    pick("chat");
  }
  // The duel screen owns a global keydown, keep typing local to this input.
  function onInputKey(e: KeyboardEvent) {
    e.stopPropagation();
    if (e.key === "Enter") { e.preventDefault(); send(); }
  }
</script>

{#snippet panel()}
  <!-- header strip: section label + tabs (.tp-chat-header / .tp-chat-tab) -->
  <div class="tp-chat-header flex items-center gap-1.5 border-b border-cyan/15 px-2.5 py-1.5">
    <span class="mr-auto font-display text-[0.5rem] font-bold uppercase tracking-[0.22em] text-cyan-dim">Comms</span>
    {#each TABS as t (t.k)}
      <button
        onclick={() => pick(t.k)}
        class="tp-chat-tab chamfer-btn relative border px-2 py-0.5 font-display text-[0.52rem] font-bold uppercase tracking-[0.14em] transition-colors {tab === t.k ? 'border-cyan/60 bg-cyan/10 text-cyan' : 'border-cyan/20 text-ink-dim hover:text-cyan'}"
        style="--c:3px"
      >
        {t.label}
        {#if t.k === "chat" && chatUnread > 0}
          <span class="absolute -right-1.5 -top-1.5 grid min-w-3.5 place-items-center rounded-full bg-magenta px-1 font-display text-[0.5rem] font-bold leading-none text-void">{chatUnread}</span>
        {/if}
      </button>
    {/each}
    {#if !docked}
      <!-- collapse the floating panel so it stops covering the board (a proper touch target) -->
      <button onclick={() => setOpen(false)} aria-label="Close comms" class="chamfer-btn grid size-6 touch:size-8 shrink-0 place-items-center border border-cyan/25 text-ink-dim transition-colors hover:border-magenta/60 hover:text-magenta" style="--c:3px">
        <svg viewBox="0 0 24 24" class="size-3" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
      </button>
    {/if}
  </div>

  <!-- feed: a solid, fully-bordered well so the lines never camouflage against the board/inspector -->
  <div
    bind:this={scroller}
    class="tp-chat-feed scroll-cyber m-2 flex h-[clamp(6.5rem,21dvh,11rem)] flex-col gap-1 overflow-y-auto chamfer bg-void px-2.5 py-1.5"
    style="--c:6px;box-shadow:inset 0 0 0 1px color-mix(in oklab,var(--color-cyan) {flashing ? 45 : 20}%,transparent)"
  >
    {#if tab === "chat"}
      {#if duel.chat.length === 0}
        <div class="grid flex-1 place-items-center text-center font-display text-[0.55rem] uppercase tracking-[0.16em] text-ink-dim/40">No messages yet</div>
      {:else}
        {#each duel.chat as m, i (i)}
          <div class="tp-chat-msg text-[0.68rem] leading-snug" in:fly={{ y: 6, duration: 140 }}>
            <span class="font-display font-semibold {m.mine ? 'text-cyan' : 'text-magenta'}">{m.fromName}:</span>
            <span class="text-ink">{m.text}</span>
          </div>
        {/each}
      {/if}
    {:else}
      {#each duel.log as line, i (i)}
        <div class="font-display text-[0.63rem] leading-tight {line.tone === 'you' ? 'text-cyan' : line.tone === 'opp' ? 'text-magenta' : line.tone === 'phase' ? 'text-gold' : 'text-ink-dim'}">{line.text}</div>
      {/each}
    {/if}
  </div>

  <!-- input -->
  <div class="px-2 pb-2">
    <input
      bind:value={draft}
      onkeydown={onInputKey}
      maxlength="200"
      placeholder="Press Enter to chat…"
      class="tp-chat-input chamfer-btn w-full bg-void/80 px-2.5 py-1 font-body text-[0.74rem] text-ink outline-none ring-1 ring-cyan/25 transition placeholder:text-ink-dim/45 focus:bg-void focus:ring-cyan/70"
      style="--c:6px"
    />
  </div>
{/snippet}

{#if docked}
  <!-- docked into the card-info rail: its own section, a top border separating it from the inspector -->
  <section class="tp-chat shrink-0 border-t border-cyan/20 bg-void-2/55">
    {@render panel()}
  </section>
{:else if open}
  <!-- floating panel (3D / narrow layouts, no rail): compact, solid, dismissible -->
  <div class="tp-chat pointer-events-auto fixed bottom-3 left-3 z-30 flex w-72 max-w-[80vw] flex-col chamfer border border-cyan/25 bg-void/95 backdrop-blur" style="--c:8px;box-shadow:0 10px 34px rgba(0,0,0,.5)" in:fly={{ y: 12, duration: 160 }}>
    {@render panel()}
  </div>
{:else}
  <!-- collapsed: a hex launcher in the corner (44px touch target) with an unread badge -->
  <button
    onclick={() => { setOpen(true); chatUnread = 0; }}
    aria-label="Open comms"
    class="tp-chat pointer-events-auto fixed bottom-3 left-3 z-30 grid size-11 touch:size-12 place-items-center chamfer border border-cyan/35 bg-void/90 text-cyan backdrop-blur transition-colors hover:border-cyan/70 hover:bg-cyan/10"
    style="--c:8px;box-shadow:0 8px 24px rgba(0,0,0,.5)"
    in:fly={{ y: 12, duration: 160 }}
  >
    <svg viewBox="0 0 24 24" class="size-5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 11.5a8.38 8.38 0 0 1-8.5 8.5 9.6 9.6 0 0 1-4-.8L3 21l1.9-5.1a8.38 8.38 0 0 1-.9-3.9A8.5 8.5 0 0 1 12.5 3 8.38 8.38 0 0 1 21 11.5z" /></svg>
    {#if chatUnread > 0}
      <span class="absolute -right-1 -top-1 grid min-w-4 place-items-center rounded-full bg-magenta px-1 font-display text-[0.55rem] font-bold leading-none text-void">{chatUnread}</span>
    {/if}
  </button>
{/if}
